import React, { Component } from "react";
import BrandAssetManager from "../../branding/BrandAssetManager";
import { formatRatingDisplay } from "./formatRatingDisplay";
import {
  deriveInactiveRatingDisplay,
  isRasterIconUrl,
} from "./deriveInactiveRatingDisplay";
import "./BrandRating.css";

/**
 * TPG Platform — shared brandable Ratings renderer.
 *
 * Icons resolve via BrandAssetManager (`ratings.icon` / `ratings.active`).
 * Components must not hard-code asset filenames. BAM returns URLs only —
 * BrandRating is format-agnostic (SVG/PNG/etc. via the same asset container).
 *
 * Inactive raster display: `deriveInactiveRatingDisplay` builds a runtime
 * blob URL that suppresses baked white outer glow and reframes solid-core
 * bounds to match `ratings.active` so select/deselect keeps an identical
 * footprint (glow/particles must not inflate the shared scale).
 * Original BAM storage / uploads are never modified. Active asset unchanged.
 *
 * Props:
 *   value / ratingValue  — current rating (supports fractions when allowFractional)
 *   max / maxRating      — scale max (default 5)
 *   inactiveIcon         — optional override URL
 *   activeIcon           — optional override URL
 *   allowFractional      — partial fills via CSS width clipping (default: true when readOnly)
 *   size                 — icon pixel size (default 28)
 *   readOnly             — display only
 *   showValue            — show numeric value
 *   hasRating            — true when a vote exists (including explicit 0)
 *   onChange(n)          — integer selection 0–max; invisible hit left of first
 *                          dove selects 0; doves 1–max select that level
 */

/**
 * Emergency inline fallback only when Brand Package URLs fail to load.
 * Prefer registry assets. Geometry mirrors the active package silhouette when
 * available; this path is last-resort only and must not be treated as branding.
 */
var DOVE_PATHS = [
  "M30.6 29.2C23.8 25.2 16.5 18.2 9.2 9.8C6.4 6.6 3.8 4.2 1.8 2.6C5.2 4.2 9.6 7.4 14.2 12.2C19.4 17.6 24.6 23.6 28.8 28.2C29.5 28.8 30.1 29.1 30.6 29.2Z",
  "M33.4 29.2C40.2 25.2 47.5 18.2 54.8 9.8C57.6 6.6 60.2 4.2 62.2 2.6C58.8 4.2 54.4 7.4 49.8 12.2C44.6 17.6 39.4 23.6 35.2 28.2C34.5 28.8 33.9 29.1 33.4 29.2Z",
  "M32 21.4C26.8 21.4 22.8 27.4 22.8 34.2C22.8 40.6 26.4 46.4 32 49.8C37.6 46.4 41.2 40.6 41.2 34.2C41.2 27.4 37.2 21.4 32 21.4Z",
  "M32.1 13.8C28.9 13.8 26.5 16.3 26.5 19.5C26.5 21.8 28 23.8 30.2 24.6C30.6 23 31.6 21.6 33 20.7C33.9 20.1 34.9 19.7 35.9 19.5V18.9C35.9 15.9 34.3 13.8 32.1 13.8Z",
  "M35.4 19.9L41.6 20.85L36.2 23.15C35.9 22.1 35.6 21 35.4 19.9Z",
  "M32 45.2C27.6 47.6 23.4 51.8 21.8 56.4C21.5 57.2 22.4 57.9 23.15 57.4C26.2 55.7 29.2 54.6 32 54.6C34.8 54.6 37.8 55.7 40.85 57.4C41.6 57.9 42.5 57.2 42.2 56.4C40.6 51.8 36.4 47.6 32 45.2Z",
];

export function resolveRatingIcons(overrides) {
  overrides = overrides || {};
  var pair = BrandAssetManager.ratingsIcons();
  var off = String(overrides.inactiveIcon || "").trim() || pair.off;
  var on = String(overrides.activeIcon || "").trim() || pair.on;
  if (!on) on = off;
  return {
    off: off,
    on: on,
    offFallback: pair.offFallback || "",
    onFallback: pair.onFallback || "",
  };
}

function DefaultDoveIcon({ filled, size }) {
  // size is optional; inside .brand-rating__icon, CSS drives the box.
  var s = size || 56;
  var gold = BrandAssetManager.brandGold();
  // Filled muted silhouette (not stroked outline) so inactive matches active geometry
  // and does not leave a light halo around gold fills in the fractional overlay.
  var fill = filled ? gold : "#B8B8B8";
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      width={s}
      height={s}
      aria-hidden="true"
      focusable="false"
    >
      {DOVE_PATHS.map(function (d, i) {
        return (
          <path
            key={i}
            d={d}
            fill={fill}
            stroke="none"
            strokeWidth={0}
          />
        );
      })}
    </svg>
  );
}

class IconGraphic extends Component {
  constructor(props) {
    super(props);
    this.state = {
      stage: "primary", // primary | fallback | inline
      displaySrc: "",
    };
    this._deriveGen = 0;
    this.handleError = this.handleError.bind(this);
  }

  componentDidMount() {
    this.syncDisplaySrc();
  }

  componentDidUpdate(prevProps) {
    var prevSrc = String(prevProps.src || "").trim();
    var nextSrc = String(this.props.src || "").trim();
    var prevFb = String(prevProps.fallbackSrc || "").trim();
    var nextFb = String(this.props.fallbackSrc || "").trim();
    var prevMatch = String(prevProps.matchSrc || "").trim();
    var nextMatch = String(this.props.matchSrc || "").trim();
    var prevFilled = !!prevProps.filled;
    var nextFilled = !!this.props.filled;
    if (
      prevSrc !== nextSrc ||
      prevFb !== nextFb ||
      prevMatch !== nextMatch ||
      prevFilled !== nextFilled
    ) {
      if (this.state.stage !== "primary") {
        this.setState({ stage: "primary" }, this.syncDisplaySrc.bind(this));
      } else {
        this.syncDisplaySrc();
      }
    }
  }

  componentWillUnmount() {
    this._deriveGen += 1;
  }

  /**
   * Inactive raster only: derive a display blob that suppresses white halo
   * and reframes solid-core bounds to match the active raster (matchSrc) so
   * select/deselect keeps an identical visual footprint. Active icons and
   * SVGs use the BAM URL directly. Derived blobs are cached per
   * (source, match) pair; originals stay in BAM storage.
   */
  syncDisplaySrc() {
    var filled = !!this.props.filled;
    var src =
      this.state.stage === "fallback"
        ? String(this.props.fallbackSrc || "").trim()
        : String(this.props.src || "").trim();
    if (!src) {
      if (this.state.displaySrc) {
        this.setState({ displaySrc: "" });
      }
      return;
    }
    if (filled || !isRasterIconUrl(src)) {
      if (this.state.displaySrc !== src) {
        this.setState({ displaySrc: src });
      }
      return;
    }
    var matchSrc = String(this.props.matchSrc || "").trim();
    var gen = ++this._deriveGen;
    var self = this;
    // Show original immediately; swap to derived when ready.
    if (this.state.displaySrc !== src) {
      this.setState({ displaySrc: src });
    }
    deriveInactiveRatingDisplay(src, matchSrc).then(function (derived) {
      if (gen !== self._deriveGen) return;
      if (self.state.displaySrc !== derived) {
        self.setState({ displaySrc: derived });
      }
    });
  }

  handleError() {
    var fallback = String(this.props.fallbackSrc || "").trim();
    if (this.state.stage === "primary" && fallback) {
      this.setState({ stage: "fallback" }, this.syncDisplaySrc.bind(this));
      return;
    }
    this.setState({ stage: "inline", displaySrc: "" });
  }

  render() {
    var s = this.props.size || 56;
    var filled = this.props.filled;
    if (this.state.stage === "inline") {
      return <DefaultDoveIcon filled={filled} size={s} />;
    }
    var src = String(this.state.displaySrc || "").trim();
    if (!src) {
      src =
        this.state.stage === "fallback"
          ? String(this.props.fallbackSrc || "").trim()
          : String(this.props.src || "").trim();
    }
    if (!src) {
      return <DefaultDoveIcon filled={filled} size={s} />;
    }
    // Inactive rasters may use a derived blob URL (halo suppressed + framed
    // to active content bounds). Active / SVG use BAM URLs as-is.
    // Active and inactive share identical width/height attributes + CSS box.
    return (
      <img
        key={this.state.stage + ":" + src}
        className="brand-rating__img"
        src={src}
        alt=""
        width={s}
        height={s}
        aria-hidden="true"
        draggable={false}
        onError={this.handleError}
      />
    );
  }
}

/**
 * Wrap artwork in the shared format-agnostic icon box.
 */
function RatingIconBox({ children }) {
  return <span className="brand-rating__icon">{children}</span>;
}

/**
 * One rating slot. fillFraction 0..1 uses CSS width clipping on the on-layer
 * (works with any image format BAM resolves — SVG, PNG, etc.).
 */
function RatingSlot({
  fillFraction,
  size,
  icons,
  interactive,
  level,
  onSelect,
  onPreview,
  onKeyDown,
}) {
  var s = size || 56;
  var fill = Math.max(0, Math.min(1, fillFraction));
  var slotStyle = { ["--brand-rating-size"]: s + "px" };
  var onClipStyle = fill < 1 ? { width: fill * 100 + "%" } : { width: "100%" };
  // Non-overlapping layers: gray must not sit under gold. AA edges of gold over
  // #B8B8B8 composite to a light rim that reads as a white border/halo.
  // fill=0: no clip-path — inset(0)/overflow:hidden both AA-fringe inactive
  // silhouettes on dark UIs. Fractional clip only when 0 < fill < 1.
  var offClipStyle =
    fill <= 0
      ? undefined
      : fill >= 1
        ? null
        : { clipPath: "inset(0 0 0 " + fill * 100 + "%)" };
  var inner = (
    <span className="brand-rating__slot" style={slotStyle}>
      {offClipStyle !== null ? (
        <span
          className="brand-rating__layer brand-rating__layer--off"
          style={offClipStyle}
          aria-hidden="true"
        >
          <RatingIconBox>
            <IconGraphic
              filled={false}
              size={s}
              src={icons.off}
              fallbackSrc={icons.offFallback}
              matchSrc={icons.on || icons.off}
            />
          </RatingIconBox>
        </span>
      ) : null}
      {fill > 0 ? (
        <span
          className="brand-rating__layer brand-rating__layer--on"
          style={onClipStyle}
          aria-hidden="true"
        >
          <RatingIconBox>
            <IconGraphic
              filled={true}
              size={s}
              src={icons.on || icons.off}
              fallbackSrc={icons.onFallback || icons.offFallback}
            />
          </RatingIconBox>
        </span>
      ) : null}
    </span>
  );

  if (!interactive) {
    return inner;
  }

  return (
    <button
      type="button"
      className="brand-rating__btn"
      aria-label={level + " of 5 Rating Icons"}
      title={level + " of 5 Rating Icons"}
      onClick={function (e) {
        e.preventDefault();
        if (onSelect) onSelect(level);
      }}
      onMouseEnter={function () {
        if (onPreview) onPreview(level);
      }}
      onFocus={function () {
        if (onPreview) onPreview(level);
      }}
      onKeyDown={function (e) {
        if (onKeyDown) onKeyDown(e, level);
      }}
    >
      {inner}
    </button>
  );
}

function slotFill(value, level, allowFractional) {
  var v = Number(value) || 0;
  if (!allowFractional) {
    return level <= Math.round(v) ? 1 : 0;
  }
  return Math.max(0, Math.min(1, v - (level - 1)));
}

class BrandRating extends Component {
  constructor(props) {
    super(props);
    // null = no hover preview; 0 = preview explicit zero (all inactive).
    this.state = { hoverValue: null };
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  handleKeyDown(event, value) {
    if (this.props.readOnly || !this.props.onChange) return;
    var max = this.props.maxRating || this.props.max || 5;
    var raw =
      this.props.ratingValue != null
        ? this.props.ratingValue
        : this.props.value;
    var current = Number(raw);
    if (!isFinite(current) || current < 0) current = 0;
    var hasRating =
      this.props.hasRating != null
        ? !!this.props.hasRating
        : raw != null && raw !== "";
    var next = value;
    if (event.key === "ArrowRight" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!hasRating) next = 1;
      else next = Math.min(max, current + 1);
      this.props.onChange(next);
    } else if (event.key === "ArrowLeft" || event.key === "ArrowDown") {
      event.preventDefault();
      if (!hasRating) {
        this.props.onChange(0);
        return;
      }
      next = Math.max(0, current - 1);
      this.props.onChange(next);
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      // Activate this level again (0–max are all valid; no delete-on-toggle).
      this.props.onChange(value);
    } else if (event.key === "Home") {
      event.preventDefault();
      this.props.onChange(0);
    } else if (event.key === "End") {
      event.preventDefault();
      this.props.onChange(max);
    }
  }

  render() {
    var max = this.props.maxRating || this.props.max || 5;
    var rawValue =
      this.props.ratingValue != null
        ? this.props.ratingValue
        : this.props.value;
    // Preserve explicit 0 (do not coerce null/undefined the same as missing).
    var value =
      rawValue == null || rawValue === ""
        ? 0
        : Number(rawValue);
    if (!isFinite(value) || value < 0) value = 0;
    var readOnly = !!this.props.readOnly;
    var size = this.props.size || 56;
    var allowFractional =
      this.props.allowFractional != null
        ? !!this.props.allowFractional
        : readOnly;
    var hoverValue = this.state.hoverValue;
    var displayValue =
      !readOnly && hoverValue != null ? hoverValue : value;
    var icons = resolveRatingIcons({
      inactiveIcon: this.props.inactiveIcon,
      activeIcon: this.props.activeIcon,
    });
    // Explicit 0 is a real rating when hasRating / rawValue is set.
    var hasExplicitRating =
      this.props.hasRating != null
        ? !!this.props.hasRating
        : rawValue != null && rawValue !== "";

    var formatted =
      hasExplicitRating || value > 0
        ? formatRatingDisplay(value)
        : "";

    var label =
      this.props["aria-label"] ||
      (formatted
        ? formatted + " of " + max + " Rating Icons"
        : "Ratings");

    var levels = [];
    for (var i = 1; i <= max; i++) {
      levels.push(i);
    }

    var self = this;
    var rootStyle = { ["--brand-rating-size"]: size + "px" };

    return (
      <span
        className={
          "brand-rating dove-rating" +
          (readOnly ? " brand-rating--readonly dove-rating--readonly" : "") +
          (this.props.className ? " " + this.props.className : "")
        }
        style={rootStyle}
        role={readOnly ? "img" : "slider"}
        aria-label={label}
        aria-valuemin={readOnly ? undefined : 0}
        aria-valuemax={readOnly ? undefined : max}
        aria-valuenow={readOnly ? undefined : value}
        aria-valuetext={
          hasExplicitRating
            ? formatRatingDisplay(value) + " of " + max + " Rating Icons"
            : "Not rated"
        }
        aria-readonly={readOnly || undefined}
        onMouseLeave={function () {
          self.setState({ hoverValue: null });
        }}
      >
        {!readOnly ? (
          <button
            type="button"
            className="brand-rating__zero-hit"
            aria-label="0 of 5 Rating Icons"
            title="0 of 5 Rating Icons"
            onClick={function (e) {
              e.preventDefault();
              if (self.props.onChange) self.props.onChange(0);
            }}
            onMouseEnter={function () {
              self.setState({ hoverValue: 0 });
            }}
            onFocus={function () {
              self.setState({ hoverValue: 0 });
            }}
            onKeyDown={function (e) {
              self.handleKeyDown(e, 0);
            }}
          />
        ) : null}
        {levels.map(function (level) {
          var fillSource =
            !readOnly && hoverValue != null ? hoverValue : displayValue;
          var fill = slotFill(
            fillSource,
            level,
            allowFractional && hoverValue == null
          );
          if (!readOnly && hoverValue != null) {
            fill = level <= hoverValue ? 1 : 0;
          } else if (!readOnly && !allowFractional) {
            // Explicit 0 → all inactive. Positive integers fill 1..n.
            fill =
              hasExplicitRating && value > 0 && level <= Math.round(displayValue)
                ? 1
                : 0;
          }
          return (
            <RatingSlot
              key={level}
              level={level}
              fillFraction={fill}
              size={size}
              icons={icons}
              interactive={!readOnly}
              onSelect={function (n) {
                if (!self.props.onChange) return;
                // Doves 1–max set that value (re-click keeps the same vote).
                // Explicit 0 is only via the left-of-first hit target.
                self.props.onChange(n);
              }}
              onPreview={function (n) {
                self.setState({ hoverValue: n });
              }}
              onKeyDown={self.handleKeyDown}
            />
          );
        })}
        {this.props.showValue && hasExplicitRating ? (
          <span
            className="brand-rating__value dove-rating__value"
            aria-hidden="true"
          >
            ({formatRatingDisplay(value)})
          </span>
        ) : null}
      </span>
    );
  }
}

export default BrandRating;
export { BrandRating, RatingSlot, DefaultDoveIcon, formatRatingDisplay };
